"use client";

import React from "react";

import currency from "currency.js";

import { useQuery } from "@tanstack/react-query";
import { useCookie } from "react-use";

import { getApiClient } from "@ide/shared/lib/apiClient";

import useCurrencyStore from "@/stores/currency";
import { abbreviateNumber } from "@ide/shared/lib/helpers";

// https://www.eurochange.co.uk/travel/tips/world-currency-abbreviations-symbols-and-codes-travel-money
import currencies from "@ide/shared/data/currencies.json";

const useCurrency = () => {
  const [value, updateCookie, deleteCookie] = useCookie("currency");
  // const [currentCurrency, setCurrentCurrency] = React.useState("AED");

  const { currentCurrency, set } = useCurrencyStore();

  React.useEffect(() => {
    set({ currentCurrency: value ?? "AED" });

    if (!value) {
      updateCookie("AED", {
        expires: Date.now() + 365 * 24 * 60 * 60 * 1000,
      });
    }
  }, [updateCookie, value, set]);

  const { data, isStale, remove } = useQuery({
    queryKey: ["currency"],
    queryFn: async () => getApiClient().get("/api/currencies"),
    // 12 hours
    cacheTime: 60 * 60 * 1000 * 12,
    staleTime: 60 * 60 * 1000 * 12,
    refetchOnReconnect: false,
  });

  const getAedValue = React.useCallback(
    (amount) => {
      if (!currentCurrency || currentCurrency === "AED") {
        return amount;
      }

      if (!data) {
        return null;
      }

      return amount / data.currencies.data[currentCurrency].value;
    },
    [data, currentCurrency],
  );

  const getCurrencyAwareAmount = React.useCallback(
    (amount) => {
      if (!currentCurrency || currentCurrency === "AED") {
        return amount;
      }

      if (!data) {
        return null;
      }

      return amount * data.currencies.data[currentCurrency].value;
    },
    [data, currentCurrency],
  );

  const format = React.useCallback(
    (val = null, params = {}) => {
      if (params.abbreviate) {
        return `${currentCurrency} ${abbreviateNumber(getCurrencyAwareAmount(val)).amount}${abbreviateNumber(getCurrencyAwareAmount(val)).suffix}`;
      }

      return currency(getCurrencyAwareAmount(val), {
        symbol: currentCurrency,
        precision: getCurrencyAwareAmount(val) < 100 ? 2 : 0,
        pattern: `! #`,
        // https://github.com/scurker/currency.js/issues/95
        negativePattern: `! -#`,
        ...params,
      }).format();
    },
    [currentCurrency, getCurrencyAwareAmount],
  );

  const currentCurrencySymbol = React.useMemo(
    () => currencies.find(({ currency }) => currency === currentCurrency ?? "AED")?.symbol ?? "د.إ",
    [currentCurrency],
  );

  return {
    getCurrencyAwareAmount,
    getAedValue,
    currentCurrency,
    currentCurrencySymbol,
    updateCurrentCurrency: (val) => {
      // clearing cache
      // remove();
      updateCookie(val);
    },
    format,
  };
};

export default useCurrency;
